/**
 * Algolia queries for job detail "similar jobs" cards and location hit count.
 * Used on SSR (serialized in page data) and on the client during CSR navigation.
 */
import type { SimilarJob } from '../../types/insights';
import { generateJobUrl } from '../routing/generate-url';
import { resolveJobUrlTitle, resolveJobUrlLocation } from '../routing/job-url-input';
import {
  buildAlgoliaLocationFacetFilter,
  buildAlgoliaNormalisedTitleFacetFilter,
} from '../search-router/build-quoted-facet-filter';
// Namespace import + unwrap: lambda/ has no "type": "module" (see server/utils/lambda-interop.ts).
import { unwrapLambdaModule } from '../../server/utils/lambda-interop';
import * as salaryRoleAliasesNs from '../../lambda/utils/salaryRoleAliases';
const { getSalaryRoleTitleVariants } = unwrapLambdaModule(salaryRoleAliasesNs);
import { companyProfileUrlPath } from '../routing/company-profile-url-path';
import { shouldLinkCompanyProfile } from '../company/should-link-company-profile';
import { resolveSearchHitIsTyomarkkinatori } from '../search/resolve-search-hit-is-tyomarkkinatori';
import { resolveSearchHitHostedLogoUrl } from '../search/resolve-search-hit-company-display';
import { resolveEffectiveBrandColour } from '../company/resolve-effective-brand-colour';

export type SimilarJobsJobLocale = 'en' | 'fi' | 'sv';

export interface SimilarJobsAlgoliaParams {
  jobObjectId: string;
  normalisedTitle?: string | undefined;
  /** First location string (same as job detail primary location). */
  primaryLocation: string;
  jobLocale: SimilarJobsJobLocale;
  indexName: string;
  /** Base URL for absolute job links (e.g. https://www.jobcrawls.com). */
  baseUrl: string;
}

/**
 * Build the single strict title filter (broadened across role-synonym variants). Requires
 * normalisedTitle; returns null if missing (better an empty result than irrelevant hits).
 *
 * Location is deliberately NOT ANDed into this filter: it used to be a separate, stricter
 * "title+location" tier that we queried first and fell back off of (a second sequential Typesense
 * round-trip whenever it came back empty). Instead, fetchSimilarJobsFromAlgolia below fetches a
 * single broader pool with this title-only filter and re-ranks location matches to the front
 * locally, which reproduces the same "prefer same-location, then anywhere" ordering in one query.
 */
export function buildSimilarJobsTitleFilter(params: {
  excludeObjectId: string;
  normalisedTitle?: string | undefined;
}): string | null {
  const trimmedTitle = params.normalisedTitle?.trim();
  if (!trimmedTitle) return null;

  const titleFilter = buildAlgoliaNormalisedTitleFacetFilter(getSalaryRoleTitleVariants(trimmedTitle));
  return `NOT objectID:${params.excludeObjectId} AND ${titleFilter}`;
}

/**
 * True when a hit's `location` (string or string[], per mapAlgoliaHitToSimilarJobCard) contains an
 * exact match for primaryLocation, mirroring buildAlgoliaLocationFacetFilter's own exact-string
 * matching semantics so the local re-rank below agrees with what the old title+location query tier
 * would have matched.
 */
function hitLocationMatches(hit: Record<string, unknown>, primaryLocation: string): boolean {
  const target = primaryLocation.trim();
  if (!target) return false;
  const location = hit.location;
  if (typeof location === 'string') return location.trim() === target;
  if (Array.isArray(location)) return location.some((l) => typeof l === 'string' && l.trim() === target);
  return false;
}

/** Attributes needed only for SimilarJobsSection cards + job URLs. */
export function attributesToRetrieveForSimilarJobCards(jobLocale: SimilarJobsJobLocale): string[] {
  return [
    'objectID',
    'title',
    `title_${jobLocale}`,
    'normalisedTitle',
    'companyId',
    'companyName',
    'location',
    'salaryRangeMin',
    'salaryRangeMax',
    'currency',
    'salaryPeriod',
    'brandColour',
    'logoUrl',
    'registryCompanyFormDescription',
    'isTyomarkkinatori',
    'remoteness',
    'jobType',
    'normalisedRoleLevel',
    'requiredLanguages',
  ];
}

export function mapAlgoliaHitToSimilarJobCard(
  hit: Record<string, unknown>,
  jobLocale: SimilarJobsJobLocale,
  baseUrl: string,
): SimilarJob | null {
  const objectID = hit.objectID != null ? String(hit.objectID) : '';
  if (!objectID) return null;
  const title = (hit[`title_${jobLocale}`] as string | undefined) || (hit.title as string | undefined);
  const companyId = hit.companyId as string | undefined;
  const companyName = typeof hit.companyName === 'string' ? hit.companyName : undefined;
  const location = hit.location as string | string[] | undefined;

  const isTyomarkkinatori = resolveSearchHitIsTyomarkkinatori(hit);
  const rawBrandColour = typeof hit.brandColour === 'string' ? hit.brandColour : undefined;
  const brandColour = resolveEffectiveBrandColour(rawBrandColour, isTyomarkkinatori);
  const hostedLogoUrl = resolveSearchHitHostedLogoUrl(hit);
  const registryCompanyFormDescription =
    typeof hit.registryCompanyFormDescription === 'string' ? hit.registryCompanyFormDescription : undefined;
  const canLinkCompanyProfile = shouldLinkCompanyProfile({ companyId, isTyomarkkinatori });

  const remoteness = typeof hit.remoteness === 'string' ? hit.remoteness : undefined;
  const jobType = typeof hit.jobType === 'string' ? hit.jobType : undefined;
  const rawRoleLevel = hit.normalisedRoleLevel;
  const normalisedRoleLevel = Array.isArray(rawRoleLevel)
    ? (rawRoleLevel as unknown[]).filter((v): v is string => typeof v === 'string' && v.trim().length > 0).join(', ')
    : typeof rawRoleLevel === 'string' ? rawRoleLevel : undefined;
  const requiredLanguages = Array.isArray(hit.requiredLanguages)
    ? (hit.requiredLanguages as unknown[]).filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    : undefined;

  return {
    objectID,
    title,
    normalisedTitle: hit.normalisedTitle as string | undefined,
    companyId,
    companyName,
    location,
    salaryRangeMin: hit.salaryRangeMin as number | undefined,
    salaryRangeMax: hit.salaryRangeMax as number | undefined,
    currency: hit.currency as string | undefined,
    salaryPeriod: hit.salaryPeriod as string | undefined,
    brandColour,
    hostedLogoUrl,
    registryCompanyFormDescription,
    isTyomarkkinatori,
    companyProfileUrl: canLinkCompanyProfile && companyId ? companyProfileUrlPath(jobLocale, companyId) : undefined,
    remoteness,
    jobType,
    normalisedRoleLevel,
    requiredLanguages,
    url: generateJobUrl({
      baseUrl,
      jobId: objectID,
      locale: jobLocale,
      company: companyId,
      title: resolveJobUrlTitle({
        normalisedTitle: hit.normalisedTitle as string | undefined,
        title,
      }),
      location: resolveJobUrlLocation({ location }),
    }),
  };
}

export function getAlgoliaSearchCredentialsFromEnv(): { appId: string; apiKey: string } | null {
  const appIdFromVite =
    (import.meta.env.PUBLIC_ENV__ALGOLIA_APP_ID as string | undefined) ||
    (import.meta.env.VITE_PUBLIC_ENV__ALGOLIA_APP_ID as string | undefined);
  const searchKeyFromVite = import.meta.env.PUBLIC_ENV__ALGOLIA_SEARCH_KEY as string | undefined;
  const appId =
    (typeof process !== 'undefined' && process.env?.PUBLIC_ENV__ALGOLIA_APP_ID) ||
    appIdFromVite;
  const apiKey =
    (typeof process !== 'undefined' &&
      (process.env?.ALGOLIA_SEARCH_KEY || process.env?.PUBLIC_ENV__ALGOLIA_SEARCH_KEY)) ||
    searchKeyFromVite;
  if (!appId || !apiKey) return null;
  return { appId: String(appId), apiKey: String(apiKey) };
}

interface SimilarJobsSearchResultLike {
  hits?: Array<Record<string, unknown>>;
  nbHits?: number;
}

/**
 * Run one similar-jobs query through the search-provider abstraction:
 * the browser client on the client, and performSsrJobSearch on the server.
 * The Algolia-style `filters` string is translated to Typesense `filter_by`
 * inside the abstraction.
 */
async function runSimilarJobsSearch(
  indexName: string,
  params: { query: string; filters?: string | null; hitsPerPage: number },
): Promise<SimilarJobsSearchResultLike | null> {
  const searchParams = {
    query: params.query,
    hitsPerPage: params.hitsPerPage,
    ...(params.filters ? { filters: params.filters } : {}),
  };

  if (typeof window !== 'undefined') {
    const { createBrowserSearchClient } = await import('../search/create-browser-search-client');
    const client = createBrowserSearchClient();
    if (!client) {
      return null;
    }
    const res = await client.search([{ indexName, params: searchParams }]);
    return ((res as { results?: SimilarJobsSearchResultLike[] })?.results?.[0]) ?? null;
  }

  const { performSsrJobSearch } = await import('../search/perform-ssr-job-search');
  return performSsrJobSearch({
    indexName,
    params: searchParams,
  });
}

const SIMILAR_JOBS_CARD_COUNT = 8;
// With a location to prefer, fetch a bigger pool than we'll show, so local re-ranking has enough
// same-location hits to fill the front of the list even if they're not Typesense's top-relevance
// matches; without a location there's nothing to re-rank, so keep the original, cheaper pool size.
const SIMILAR_JOBS_POOL_SIZE_WITH_LOCATION = 24;

export async function fetchSimilarJobsFromAlgolia(
  params: SimilarJobsAlgoliaParams,
): Promise<{ similarJobs: SimilarJob[] }> {
  const titleFilter = buildSimilarJobsTitleFilter({
    excludeObjectId: params.jobObjectId,
    normalisedTitle: params.normalisedTitle,
  });
  if (!titleFilter) {
    return { similarJobs: [] };
  }

  const hasLocation = !!params.primaryLocation?.trim();
  const hitsPerPage = hasLocation ? SIMILAR_JOBS_POOL_SIZE_WITH_LOCATION : SIMILAR_JOBS_CARD_COUNT;

  // Single query, title-only filter: what used to be a "title+location" tier queried first (with
  // a sequential title-only fallback only if it came back empty) is now one broader-pooled query,
  // re-ranked locally below so same-location hits still lead when one exists.
  const [similarResult] = await Promise.allSettled([
    runSimilarJobsSearch(params.indexName, { query: '', filters: titleFilter, hitsPerPage }),
  ]);

  let similarJobs: SimilarJob[] = [];

  if (similarResult.status === 'fulfilled') {
    let hits = (similarResult.value?.hits ?? []) as Array<Record<string, unknown>>;
    if (hasLocation) {
      // Stable sort: same-location hits first, otherwise Typesense's own relevance order is kept
      // within each group.
      hits = hits
        .map((hit, index) => ({ hit, index }))
        .sort((a, b) => {
          const aMatch = hitLocationMatches(a.hit, params.primaryLocation) ? 0 : 1;
          const bMatch = hitLocationMatches(b.hit, params.primaryLocation) ? 0 : 1;
          if (aMatch !== bMatch) return aMatch - bMatch;
          return a.index - b.index;
        })
        .map(({ hit }) => hit);
    }
    for (const h of hits.slice(0, SIMILAR_JOBS_CARD_COUNT)) {
      const card = mapAlgoliaHitToSimilarJobCard(h, params.jobLocale, params.baseUrl);
      if (card) similarJobs.push(card);
    }
  }

  return { similarJobs };
}
